// hooks/useKnowledgeBases.js
import { useCallback, useEffect, useRef, useState } from 'react';
import KnowledgeBasesService from '../services/knowledgeBases.service';
import { useOrganizations } from './useOrganizations';

const DEFAULT_PAGE_SIZE = 50;

/**
 * Listado de Knowledge Bases del account actual.
 *
 * El endpoint `GET /kbs` pagina por `limit`/`offset` y devuelve
 * `{ items, total, limit, offset }`. El hook pide la primera ventana al
 * montar y expone `loadNextPage` para appendear más sin perder orden.
 *
 * Devuelve un shape paralelo a `useTables`:
 *   - `knowledgeBases`: filas acumuladas.
 *   - `totalCount`: total del backend.
 *   - `hasMore`: si quedan más por cargar.
 *   - `loading` / `loadingMore` / `error`.
 *   - `refetch`: vuelve al offset 0.
 *   - `loadNextPage`: avanza una ventana.
 *
 * Reset cuando cambia `accountId`. Cancela requests en vuelo con AbortSignal.
 */
export function useKnowledgeBases({ pageSize = DEFAULT_PAGE_SIZE } = {}) {
  const { currentAccount } = useOrganizations();
  const accountId = currentAccount?.id;

  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);

  const fetchWindow = useCallback(
    async (targetOffset, { append }) => {
      if (!accountId) {
        setKnowledgeBases([]);
        setTotalCount(0);
        setHasMore(false);
        setOffset(0);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      const result = await KnowledgeBasesService.listKnowledgeBases(accountId, {
        limit: pageSize,
        offset: targetOffset,
      });

      if (controller.signal.aborted) return;

      if (result.success) {
        const incoming = result.data?.items ?? [];
        const total = result.data?.total ?? incoming.length;
        setKnowledgeBases((prev) => (append ? [...prev, ...incoming] : incoming));
        setTotalCount(total);
        setOffset(targetOffset + incoming.length);
        setHasMore(targetOffset + incoming.length < total);
      } else {
        setError(result.error);
      }

      if (append) setLoadingMore(false);
      else setLoading(false);
    },
    [accountId, pageSize]
  );

  const refetch = useCallback(() => fetchWindow(0, { append: false }), [fetchWindow]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    fetchWindow(offset, { append: true });
  }, [fetchWindow, hasMore, loading, loadingMore, offset]);

  useEffect(() => {
    fetchWindow(0, { append: false });
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchWindow]);

  return {
    knowledgeBases,
    totalCount,
    hasMore,
    loading,
    loadingMore,
    error,
    refetch,
    loadNextPage,
  };
}

export default useKnowledgeBases;
